import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ITicker, IWatchedTicker, Network, enums } from '@kattana-io/common';
import SmallCurrencyIcon from '../../Trading/common/SmallCurrencyIcon';
import { IStore } from '../../../reducers';
import useTicker from '../../../hooks/useTicker';
import Text from '../../../atoms/Text';
import Loader from '../../common/Loader';
import { decorateExchangeName } from '../../../helpers/decorators';
import PoolRow from '../PoolRow';
import PoolLinks from '../PoolLinks';
import { ICurrentState } from '../../../reducers/current';
import { IPoolWidgetLinks } from '../useTickerPool';
import { PoolWidgetRow } from '../helpers/rows';
import { switchCurrentTicker } from '../../../Authlogic';
import {
  actionAddWatchlist,
  actionRemoveWatchlist,
  actionUpdateWatchlist,
} from '../../../actions/watchlist';
import WatchlistLiqRow from '../../Watchlist/WatchlistLiqRow';

import './styles.scss';
import { UpdateWatchlistItemPayload } from '../../../reducers/watchlist';

interface IPoolInfoProps {
  current: ICurrentState;
  loading: boolean;
  links?: IPoolWidgetLinks | null;
  rows: PoolWidgetRow[];
  securityRows: PoolWidgetRow[];
  sameBaseTickers: ITicker[];
  mobile?: boolean;
}

interface IChainExchangeDecoratorProps {
  exchange: string;
  chain: string;
}

/**
 * Decorates exchange/chain title
 * Hide exchange name if it is orphan
 */
const ChainExchangeDecorator = ({
  exchange,
  chain,
}: IChainExchangeDecoratorProps) => {
  const exchangePart = decorateExchangeName(exchange, chain);
  const chainPart = Network.isValidChain(chain)
    ? Network.fromChain(chain).getTitle()
    : chain;
  const combined = chainPart
    ? `${exchangePart} ( ${chainPart} )`
    : exchangePart;
  return (
    <Text
      size={12}
      className='pool_info__header__subtitle'
      style={{ title: combined }}
    >
      {combined}
    </Text>
  );
};

const PoolInfo = ({
  current,
  loading,
  links,
  rows,
  securityRows,
  sameBaseTickers,
  mobile,
}: IPoolInfoProps) => {
  const timezone = useSelector((state: IStore) => state.timezone.current);
  const watchedTickers = useSelector(
    (state: IStore) => state.watchlist.watched
  );

  const [goPlusSecurity, setGoPlusSecurity] = useState(false);

  const ticker = useTicker(current);
  const dispatch = useDispatch();

  // hides go plus security on ticker switch
  useEffect(() => {
    setGoPlusSecurity(false);
  }, [current.id]);

  const onSwitch = useCallback((payload: any) => {
    switchCurrentTicker(payload);
  }, []);

  const onRemove = useCallback(
    (index: number, callback: () => void) =>
      dispatch(actionRemoveWatchlist(index, callback)),
    [dispatch]
  );

  const onAdd = useCallback(
    (item: ITicker, tag: enums.Tag, callback: () => void) =>
      dispatch(actionAddWatchlist(item, tag, callback)),
    [dispatch]
  );

  const onUpdate = useCallback(
    (id: number, payload: UpdateWatchlistItemPayload) => {
      dispatch(actionUpdateWatchlist(id, payload));
    },
    [dispatch]
  );

  const network = Network.fromChain(current.chain);

  return (
    <div className='pool_info'>
      {!mobile && (
        <div className='pool_info__header'>
          <div
            className={`pool_info__header__title ${
              current.kind === enums.ETickerKind.CEX
                ? 'pool_info__header__title--disabled'
                : ''
            }`}
          >
            <Link to={`/en/token/${ticker.chain}/${ticker.base_address}`}>
              <SmallCurrencyIcon
                network={network}
                currency={ticker.base}
                contract={ticker.base_address}
                variant='s'
              />
              <Text size={18} className='pool_info__header__title--base'>
                {ticker.base}
              </Text>
            </Link>
            <Text size={18} className='pool_info__header__title--slash'>
              &nbsp;/&nbsp;
            </Text>
            <Link to={`/en/token/${ticker.chain}/${ticker.quote_address}`}>
              <SmallCurrencyIcon
                network={network}
                currency={ticker.quote}
                contract={ticker.quote_address}
                variant='s'
              />
              <Text size={18} className='pool_info__header__title--quote'>
                {ticker.quote}
              </Text>
            </Link>
          </div>
          <ChainExchangeDecorator
            chain={current.chain}
            exchange={current.exchange}
          />
        </div>
      )}
      {!mobile && (
        <PoolLinks
          website={links?.website}
          coingecko_id={links?.coingecko_id}
          coinmarketcap_id={links?.coinmarketcap_id}
          telegram={links?.telegram}
          twitter_id={links?.twitter_id}
          logo={links?.logo}
          ticker={ticker}
          network={network}
        />
      )}
      {loading ? (
        <div className='pool_info__loader'>
          <Loader />
        </div>
      ) : (
        <div className='pool_info__body'>
          {rows.map((row) => (
            <PoolRow
              key={row.id}
              row={row}
              network={network}
              timezone={timezone}
              goPlusSecurity={goPlusSecurity}
              toggleGoPlusSecurity={() => setGoPlusSecurity(!goPlusSecurity)}
            />
          ))}
          {goPlusSecurity && (
            <div className='pool_info__body__extra_rows'>
              {!mobile && (
                <div className='watchlist__search_divider'>
                  <Text
                    size={14}
                    className='watchlist__search_divider__search text--grey-400'
                  >
                    Go+ Security Statistics
                  </Text>
                </div>
              )}
              {securityRows.map((row) => (
                <PoolRow
                  key={row.id}
                  row={row}
                  network={network}
                  timezone={timezone}
                />
              ))}
            </div>
          )}
          {!!sameBaseTickers.length && (
            <div className='pool_info__body__extra_rows'>
              <div className='watchlist__search_divider'>
                <Text
                  size={14}
                  className='watchlist__search_divider__search text--grey-400'
                >
                  Pairs
                </Text>
              </div>
              {sameBaseTickers.map((t) => {
                const isInWatchList = watchedTickers.some(
                  (ticker) => ticker.id === t.id
                );
                return (
                  <WatchlistLiqRow
                    key={t.id}
                    item={t}
                    active={current.id === t.id}
                    actionSwitchCurrent={onSwitch}
                    actionRemoveWatchlist={isInWatchList ? onRemove : undefined}
                    actionAddWatchlist={isInWatchList ? undefined : onAdd}
                    actionUpdateWatchlist={isInWatchList ? onUpdate : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PoolInfo;
